import { useCallback, useEffect, useRef, useState } from 'react';

import { Statistic } from 'src/types/components';

import Text from 'components/BaseHelpers/Text';

type AnimatedStatProps = {
  stat: Exclude<Statistic['stat'], null>;
};

const duration = 400;

export const AnimatedStat = ({ stat }: AnimatedStatProps) => {
  const ref = useRef(null);
  const [animatedStat, setAnimatedStat] = useState(stat);
  const hasAnimated = useRef(false);

  const animate = useCallback(() => {
    let start: number | undefined;
    let previousTimeStamp: number | undefined;

    const step: FrameRequestCallback = (timestamp) => {
      if (start === undefined) {
        start = timestamp;
      }
      const elapsed = timestamp - start;

      if (previousTimeStamp !== timestamp) {
        const newStat = stat
          .split('')
          .map((character) => {
            const number = parseInt(character);
            if (!isNaN(number)) {
              return Math.floor(Math.random() * 10);
            }

            return character;
          })
          .join('');

        setAnimatedStat(newStat);
      }

      if (elapsed < duration) {
        requestAnimationFrame(step);
      } else {
        setAnimatedStat(stat);
      }
    };

    requestAnimationFrame(step);
  }, [stat]);

  const triggerAnimation = useCallback(() => {
    if (hasAnimated.current) {
      return;
    }
    hasAnimated.current = true;
    animate();
  }, [hasAnimated, animate]);

  useEffect(() => {
    // check to see if stat is parsed as a number
    const parsedStat = parseInt(stat);
    const shouldAnimate = !isNaN(parsedStat);
    if (!shouldAnimate) {
      return;
    }

    triggerAnimation();
    // only run on mount and dismount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Text ref={ref} field={animatedStat} />;
};
